import get from "lodash/get";

import {
	StreamDocumentType,
	TeamMemberStatusType,
	TeamRoleType,
	TeamType,
	TemplateCategory,
	TemplateScope
} from "@teaming/common-enums";

const templateTypes = Object.freeze( {
	[ TeamType.TEAM ]: "TEAM",
	[ TeamType.ONE_ON_ONE ]: "1:1",
} );

const templateScopes = Object.freeze( {
	[ TeamType.TEAM ]: TemplateScope.TEAM,
	[ TeamType.ONE_ON_ONE ]: TemplateScope.ONE_ON_ONE,
} );

const templateCategories = Object.freeze( {
	[ TeamType.TEAM ]: TemplateCategory.TEAM_TEMPLATES,
	[ TeamType.ONE_ON_ONE ]: TemplateCategory.ONE_ON_ONE_TEMPLATES,
} );

const documentNamePrefixes = Object.freeze( {
	[ TeamType.TEAM ]: "river",
	[ TeamType.ONE_ON_ONE ]: "stream",
} );

const highTypeSortOrder = Object.freeze( {
	D: 0,
	I: 1,
	S: 2,
	C: 3,
} );

function highTypeSortComparator( a, b ) {
	return highTypeSortOrder[ a ] - highTypeSortOrder[ b ];
}

/* @TODO: move computation to services */
const computeTeamWorkStyle = ( { team, } ) => {
	const discAssessments = team.members.map( member => {
		const { assessments, } = member;

		const discAssessment = assessments?.DISC || {
			moniker: "",
		};

		const { moniker, } = discAssessment;
		const highType = moniker.slice( 0, 1 ).toUpperCase();

		return {
			highType,
			isComplete: !!moniker,
			...discAssessment,
		};
	} );

	const completedAssessments = discAssessments.filter( assessment => !!assessment.isComplete );

	team.workStyle = [
		...completedAssessments.reduce( ( acc, assessment ) => {
			acc.add( assessment.highType );
			return acc;
		}, new Set() ),
	].sort( highTypeSortComparator ).join( "" );

	return team;
};

const prototypeMethods = {
	getActiveLeadMembers() {
		return this.getLeadMembers().filter( member => member.status === TeamMemberStatusType.ACTIVE );
	},
	getActiveMember( memberId ) {
		const member = this.getMember( memberId );
		return ( member && member.status === TeamMemberStatusType.ACTIVE ) ? member : null;
	},
	getActiveMembers() {
		return this.getMembers().filter( member => member.status === TeamMemberStatusType.ACTIVE );
	},
	getCreator() {
		return this.getMembers().find( member => member.personId === this.createdBy );
	},
	getDocumentNamePrefix() {
		return documentNamePrefixes[ this.type ];
	},
	getExtendedName( memberId ) {
		let teamName = this.name;
		if ( this.isOneOnOne() && memberId ) {
			const thisMember = this.getMember( memberId );
			const otherMember = this.getOtherMembers( memberId )[ 0 ];
			const otherMemberName = otherMember.firstName || otherMember.email;
			teamName = `${ otherMemberName } : ${ thisMember.firstName }`;
		}
		return teamName;
	},
	getInvitedMember( memberId ) {
		const member = this.getMember( memberId );
		return ( member && member.status === TeamMemberStatusType.INVITED ) ? member : null;
	},
	getInviterId( memberId ) {
		const invitedBy = ( this.getMember( memberId ) || {} ).invitedBy || "";
		// le sigh, this could be an object
		return invitedBy.personId || invitedBy;
	},
	getLeadMember( memberId ) {
		if ( !memberId ) {
			return this.getLeadMembers()[ 0 ];
		}
		return this.getLeadMembers().find( member => member.personId === memberId ) || null;
	},
	getLeadMembers() {
		const leadRole = this.isOneOnOne() ? TeamRoleType.MANAGER : TeamRoleType.LEAD;
		return this.getMembers().filter( member => member.role === leadRole );
	},
	getLegacyOneOnOneName() {
		if ( !this.isOneOnOne() ) {
			return "";
		}
		const lead = this.getLeadMember();
		const report = this.getOtherMembers( lead.personId )[ 0 ];
		const leadFirstName = lead.displayName.split( " " )[ 0 ];
		const reportFirstName = ( report.displayName || report.email ).split( " " )[ 0 ];
		return `${ leadFirstName } : ${ reportFirstName }`;
	},
	getMember( memberId ) {
		return this.getMembers().find( member => member.personId === memberId );
	},
	getMembers() {
		return this.members.map( member => {
			return {
				...member,
				displayName: member.displayName || "",
			};
		} );
	},
	getMoniker( memberId ) {
		return this.getMonikers()[ memberId ] || "";
	},
	getMonikers() {
		return this.getMembers().reduce( ( monikers, member ) => {
			monikers[ member.personId ] = get( member, "assessments.DISC.moniker" ) || "";
			return monikers;
		}, {} );
	},
	getName( memberId ) {
		let teamName = this.name;
		if ( this.isOneOnOne() && memberId ) {
			const otherMember = this.getOtherMembers( memberId )[ 0 ];
			teamName = otherMember.displayName || otherMember.email;
		}
		return teamName;
	},
	getOtherActiveLeadMembers( memberId ) {
		const activeLeadMembers = this.getActiveLeadMembers();
		return activeLeadMembers.filter( member => member.personId !== memberId );
	},
	getOtherActiveMembers( memberId ) {
		return this.getOtherMembers( memberId ).filter( member => member.status === TeamMemberStatusType.ACTIVE );
	},
	getOtherMembers( memberId ) {
		return this.getMembers().filter( member => member.personId !== memberId );
	},
	getPrivateDocumentName( memberId ) {
		const prefix = this.getDocumentNamePrefix();
		return `${ prefix }.${ this.id }.${ StreamDocumentType.PRIVATE }.${ memberId }`;
	},
	getSharedDocumentName() {
		const prefix = this.getDocumentNamePrefix();
		return `${ prefix }.${ this.id }.${ StreamDocumentType.SHARED }`;
	},
	getTemplateCategory() {
		return templateCategories[ this.type ];
	},
	getTemplateScope() {
		return templateScopes[ this.type ];
	},
	getTemplateType() {
		return templateTypes[ this.type ];
	},
	isLeadMember( memberId ) {
		return !!( memberId && this.getLeadMember( memberId ) );
	},
	isOneOnOne() {
		return this.type === TeamType.ONE_ON_ONE;
	},
};

export const extendTeam = ( { team, } ) => {
	const teamMethods = Object.create( prototypeMethods );
	return Object.assign( teamMethods, computeTeamWorkStyle( { team, } ) );
};
